import { useAppSelector } from 'modules/Editor/store';
import { selectActiveWidgetIds, selectActiveWidgets } from 'modules/Editor/store/widgetSelector';
import { createContext, useCallback, useEffect, useRef, useState } from 'react';
import {
  GroupTargetData,
  NodeCreatorModalData,
  PortContextI,
  PortProviderProps,
  PortState,
  PortTypes,
  PortWrapperData,
} from 'types/flowCore.types';
import { WidgetId } from 'types/idTypes';
import { WidgetType } from 'types/widget.types';
import { calculateAllParentWidgetOffset, getElementForWidgetId, getWidgetPosition, getWidgetRotation } from 'utils/dom';
import { proxy } from 'valtio';
import { getWidgetTypeFromId } from 'widgets/Widget.helpers';
import { groupIdCache } from 'widgets/sdk/GroupIdCache';
import { lineConnectionCache } from 'widgets/sdk/LineConnectionCache';

import { useFlow } from './FlowCore.hooks';
import { useGetWidgetDataRect } from './components/PortStepsGrid.hooks';

export const PortContext = createContext<PortContextI>({
  value: {
    creatorPortData: null,
    connectorPortData: null,
    selectedPort: null,
    nodeModalData: null,
    getSelectedPort: () => null,
    setSelectedPort: () => {},
    renderPort: () => {},
    clearPort: () => {},
    renderModal: () => {},
    closeModal: () => {},
    renderGroupPort: () => {},
    updatePortPosition: () => {},
  },
});

/**
 * Manages state related to port rendering only
 *
 * @param {PortProviderProps}
 * @returns
 */
export const PortProvider = ({ children }: PortProviderProps): JSX.Element => {
  const [selectedPort, setSelectedPort] = useState<PortState | null>(null);
  const [creatorPortData, setCreatorPortData] = useState<PortWrapperData | null>(null);
  const [connectorPortData, setConnectorPortData] = useState<PortWrapperData | null>(null);
  const [nodeModalData, setNodeModalData] = useState<NodeCreatorModalData | null>(null);

  const activeWidgetIds = useAppSelector(selectActiveWidgetIds);
  const activeWidgets = useAppSelector(selectActiveWidgets);

  const { getConnectedLines } = useFlow();
  const isSingleSelect = useRef(false); // Use the useRef for the nextTick in the useEffect
  const { getRectOfGroupFromWidgetId } = useGetWidgetDataRect();

  // sync these values in refs so that they can be used in the renderPort
  // without the need of passing them as dependencies
  const connectorPortSyncRef = useRef<PortWrapperData | null>(null);
  connectorPortSyncRef.current = connectorPortData;
  const selectedPortSyncRef = useRef<PortState | null>(null);
  selectedPortSyncRef.current = selectedPort;

  const getSelectedPort = useCallback(() => selectedPortSyncRef.current, []);

  // Render the LineCreator or LineConnector port
  const renderPort = useCallback(
    (
      targetElm: HTMLElement | null,
      type: PortTypes,
      groupWrapperData?: GroupTargetData,
      includeCenter?: boolean,
    ): void => {
      const widgetId = targetElm?.id;

      const isLineWidget = widgetId && getWidgetTypeFromId(widgetId) === WidgetType.Line;
      const isConnector = type === PortTypes.Connector;
      const resetPortConnector = isConnector && connectorPortSyncRef.current !== null && targetElm === null;
      const setPortData = isConnector ? setConnectorPortData : setCreatorPortData;

      if (targetElm === null && !groupWrapperData) {
        if (isConnector) setPortData(null);
        return;
      }

      if (groupWrapperData) {
        setPortData({
          widgetId: groupWrapperData.groupId,
          position: groupWrapperData.wrapperPos,
          rotation: 0, // TODO: handle rotation.
          includeCenter,
        });
      } else if (targetElm) {
        // Calculate all parents offset for the group and responsive widget
        const wrapperPosition = getWidgetPosition(targetElm);
        const wrapperRotation = getWidgetRotation(targetElm);
        const parentOffset = calculateAllParentWidgetOffset(targetElm);
        wrapperPosition.leftPx += parentOffset.leftPx;
        wrapperPosition.topPx += parentOffset.topPx;

        if (widgetId && !isLineWidget && !resetPortConnector) {
          setPortData({
            widgetId,
            position: wrapperPosition,
            rotation: wrapperRotation,
            includeCenter,
          });
        }
      }
    },
    [setConnectorPortData],
  );

  /**
   * Call this to update the port position based on the widget's current dimensions
   */
  const updatePortPosition = useCallback(
    (widgetId: WidgetId) => {
      // Render the ports again to update their position based on the new widget size
      // Only if the widget is attached to a line
      const groupWidgetId = groupIdCache.getParentId(widgetId);
      const isAttachedToLines = lineConnectionCache.getAttachedLines(groupWidgetId || widgetId);
      if (!isAttachedToLines || !isAttachedToLines.length) return;
      const element = getElementForWidgetId(groupWidgetId || widgetId) as HTMLElement;
      if (!element) return;

      renderPort(element, PortTypes.Creator);
    },
    [renderPort],
  );

  // Reset the port rendering
  const clearPort = useCallback(() => {
    setSelectedPort(null);
    setCreatorPortData(null);
    setConnectorPortData(null);
    setNodeModalData(null);
  }, []);

  // Render line creator when the single widget is selected
  // In the BoundingBox event, it is difficult to control due to the Selecto and Moveable library race conditions
  useEffect(() => {
    isSingleSelect.current = activeWidgets.length === 1;
    if (!isSingleSelect.current) {
      clearPort();
      return;
    }

    const targetElement = getElementForWidgetId(activeWidgetIds[0]);
    const widgetId = activeWidgetIds[0];
    const widgetType = getWidgetTypeFromId(widgetId);
    const isGroupWidget = widgetType === WidgetType.Group;
    const isLineWidget = widgetType === WidgetType.Line;
    const connectedLinesToThisWidget = getConnectedLines(widgetId, targetElement as HTMLElement) || [];
    // Don't show ports if the widget is a group or line widget or if there are no connected lines to this widget
    if (isLineWidget || connectedLinesToThisWidget.length === 0) {
      clearPort();
      return;
    }

    // Use nextTick for undo/redo
    setTimeout(() => {
      if (!isSingleSelect.current) return;
      if (!targetElement) return;

      const targetPosition = getWidgetPosition(targetElement);
      const targetRotation = getWidgetRotation(targetElement);

      if (isGroupWidget) {
        const groupWrapperPos = getRectOfGroupFromWidgetId(widgetId);
        if (groupWrapperPos) {
          setCreatorPortData({
            widgetId: widgetId,
            position: groupWrapperPos,
            rotation: 0, // TODO: handle rotation.
          });
        }
        return;
      }

      setCreatorPortData({
        widgetId: targetElement?.id,
        position: targetPosition,
        rotation: targetRotation,
      });
    }, 0);
  }, [activeWidgets, activeWidgetIds, clearPort, getConnectedLines, getRectOfGroupFromWidgetId]);

  const renderGroupPort = useCallback(
    (widgetId: string) => {
      const groupWrapperPos = getRectOfGroupFromWidgetId(widgetId);

      if (groupWrapperPos) {
        setCreatorPortData({
          widgetId: widgetId,
          position: groupWrapperPos,
          rotation: 0, // TODO: handle rotation.
        });
      }
    },
    [getRectOfGroupFromWidgetId],
  );

  // Render the node creator modal component
  const handleRenderModal = useCallback(
    (nodeCreatorModalData: NodeCreatorModalData | null) => setNodeModalData(nodeCreatorModalData),
    [],
  );

  // Clear the node creator modal component
  const handleCloseModal = useCallback(() => setNodeModalData(null), []);

  const value = {
    connectorPortData,
    creatorPortData,
    selectedPort,
    nodeModalData,
    getSelectedPort,
    setSelectedPort,
    renderPort,
    clearPort,
    renderModal: handleRenderModal,
    closeModal: handleCloseModal,
    renderGroupPort,
    updatePortPosition,
  };

  const state = useRef<PortContextI>(proxy({ value })).current;
  // sync the values with the proxy ref
  state.value = value;

  return <PortContext.Provider value={state}>{children}</PortContext.Provider>;
};
